import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { format } from "date-fns";
import { payWithRazorpay } from "@/lib/razorpay";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import {
  Loader2, Sparkles, TrendingDown, AlertTriangle, ListChecks, Upload, FileText, Image as ImageIcon,
  Trash2, Send, CalendarIcon, ArrowLeft, ArrowRight, IndianRupee, Bot, User as UserIcon, FileSpreadsheet,
  Gauge, CheckCircle2, Clock, CircleDot, LayoutDashboard, Calculator, ShieldCheck, FileDown, Plus, Paperclip
} from "lucide-react";

const COLORS = ["#0E9AC8", "#0F172A", "#475569", "#94A3B8", "#CBD5E1", "#F5923C", "#7C3F22"];
const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const compactINR = (n) => n >= 1e7 ? `₹${(n / 1e7).toFixed(2)} Cr` : n >= 1e5 ? `₹${(n / 1e5).toFixed(1)} L` : fmt(n);
const sevColor = { high: "bg-red-50 text-red-700 border-red-200", medium: "bg-amber-50 text-amber-700 border-amber-200", low: "bg-slate-50 text-slate-600 border-slate-200" };
const CARD = "card-premium rounded-2xl";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [docs, setDocs] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [focusDoc, setFocusDoc] = useState(null);
  const [pendingAnalyze, setPendingAnalyze] = useState(null);

  const onAnalyze = useCallback((doc) => {
    setFocusDoc(doc);
    setPendingAnalyze({ doc, nonce: Date.now() });
    setTab("chat");
  }, []);

  const loadProject = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${id}`);
      setProject(res.data);
    } catch {
      toast.error("Project not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const refreshMeta = useCallback(async () => {
    const [d, c] = await Promise.all([api.get(`/projects/${id}/documents`), api.get("/consultations")]);
    setDocs(d.data);
    setConsultations(c.data.filter((x) => x.project_id === id));
  }, [id]);

  useEffect(() => { loadProject(); refreshMeta(); }, [loadProject, refreshMeta]);

  const onDocChange = useCallback(async () => {
    await refreshMeta();
    try {
      toast.message("Updating your Construction Health Score…");
      const res = await api.post(`/projects/${id}/generate-report`);
      setProject((p) => ({ ...p, report: res.data }));
      toast.success(`Health score updated: ${res.data?.overall_score ?? ""}`);
    } catch {
      toast.error("Couldn't auto-update the health score. You can regenerate it from the Health Report tab.");
    }
  }, [id, refreshMeta]);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/projects/${id}/generate-report`);
      setProject((p) => ({ ...p, report: res.data }));
      toast.success("Construction Health Report ready");
    } catch {
      toast.error("Failed to generate report. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#FBFAF9]"><Navbar /><div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div></div>;

  const report = project.report;
  const tabs = [["overview", "Overview"], ["report", "Health Report"], ["estimates", "Estimates"], ["documents", "Documents"], ["chat", "AI Advisor"], ["consult", "Consultation"]];

  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <button onClick={() => navigate("/dashboard")} data-testid="back-button"
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0E9AC8] transition-colors mb-5">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> Back to dashboard
        </button>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl lg:text-4xl tracking-tighter font-extrabold text-slate-900">{project.name}</h1>
            <p className="mt-2 text-slate-500">
              {project.city} · {project.plot_area} {project.plot_unit} · {project.floors} floor(s)
              {project.est_construction_area ? ` · ${project.est_construction_area.toLocaleString("en-IN")} sq ft est. area` : ""}
              {project.budget ? ` · Budget ${compactINR(project.budget)}` : ""}
            </p>
          </div>
          {report && <span className="text-sm font-bold px-3 py-1.5 rounded-full bg-[#0E9AC8]/10 text-[#0E9AC8] w-fit">Health {report.overall_score}/100</span>}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-xl bg-white border border-slate-200 p-1 h-auto flex-wrap">
            {tabs.map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
                className="rounded-lg data-[state=active]:bg-[#0E9AC8] data-[state=active]:text-white px-4 py-2 text-sm transition-colors">
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <OverviewTab project={project} docs={docs} consultations={consultations} onTab={setTab} onGenerate={() => setTab("report")} onBook={() => setTab("consult")} />
          </TabsContent>

          <TabsContent value="report" className="mt-6">
            {!report ? (
              <div className={`${CARD} p-16 text-center`} data-testid="no-report">
                <Sparkles className="h-10 w-10 text-[#0E9AC8] mx-auto mb-4" strokeWidth={1.25} />
                <h3 className="font-display text-xl font-bold text-slate-900">Generate your Construction Health Report</h3>
                <p className="mt-2 text-slate-500 max-w-md mx-auto">Our AI analyses your project details and uploaded documents to score your build, estimate cost, and flag risks.</p>
                <Button data-testid="generate-report-button" onClick={generateReport} disabled={generating}
                  className="mt-6 bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-11 px-6 transition-colors">
                  {generating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analysing…</> : <><Sparkles className="mr-2 h-4 w-4" /> Generate Report</>}
                </Button>
              </div>
            ) : <ReportView report={report} onRegenerate={generateReport} generating={generating} />}
          </TabsContent>

          <TabsContent value="documents" className="mt-6"><DocumentsTab projectId={id} docs={docs} onChange={onDocChange} onAnalyze={onAnalyze} /></TabsContent>
          <TabsContent value="estimates" className="mt-6"><EstimatesTab projectId={id} /></TabsContent>
          <TabsContent value="chat" className="mt-6"><ChatTab projectId={id} projectName={project.name} onDocUploaded={onDocChange} focusDoc={focusDoc} onFocusDoc={setFocusDoc} pendingAnalyze={pendingAnalyze} onAnalyzed={() => setPendingAnalyze(null)} /></TabsContent>
          <TabsContent value="consult" className="mt-6"><ConsultTab projectId={id} onBooked={refreshMeta} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function OverviewTab({ project, docs, consultations, onTab, onGenerate, onBook }) {
  const report = project.report;
  const rec = !docs.length && !report
    ? { label: "Upload your BOQ, drawing or quotation", desc: "NCIS™ analyses your documents to flag risks, missing items and inflated costs.", cta: "Upload Documents", act: () => onTab("documents") }
    : docs.length && !report
    ? { label: "Generate your Construction Health Report", desc: "Get an instant score, cost estimate, risks and savings based on your project and documents.", cta: "Generate Report", act: () => onTab("report") }
    : { label: `Ask NCIS™ about ${project.name}`, desc: "Ask about your costs, quotations, materials or risks — and find the right vendors when needed.", cta: "Ask NCIS™", act: () => onTab("chat") };
  const savings = report ? (report.optimizations || []).reduce((a, o) => a + (o.potential_saving_inr || 0), 0) : 0;
  const CONSULT_STATUS = {
    paid_confirmed: { label: "Paid — Confirmed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    pending_payment: { label: "Payment pending", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    requested: { label: "Requested", cls: "bg-slate-50 text-slate-600 border-slate-200" },
  };
  const latestConsult = consultations.find((c) => c.status === "paid_confirmed") || consultations[0];
  const consultMeta = latestConsult ? (CONSULT_STATUS[latestConsult.status] || CONSULT_STATUS.requested) : null;
  const scoreColor = !report ? "#94A3B8" : report.overall_score >= 80 ? "#16a34a" : report.overall_score >= 60 ? "#0E9AC8" : "#dc2626";

  const timeline = [
    { label: "Project created", date: project.created_at, done: true },
    { label: "Documents uploaded", date: docs[0]?.created_at, done: docs.length > 0, hint: `${docs.length} file(s)` },
    { label: "Health Report generated", date: report?.generated_at, done: !!report },
    { label: "Expert consultation", date: latestConsult?.preferred_date, done: latestConsult?.status === "paid_confirmed", hint: latestConsult ? (consultMeta?.label || latestConsult.status) : "Not booked" },
  ];

  const tiles = [
    { icon: Gauge, label: "Construction Health Score", value: report ? `${report.overall_score}/100` : "—", sub: report?.score_label || "Generate report", color: scoreColor },
    { icon: IndianRupee, label: "Estimated Construction Cost", value: report ? compactINR(report.estimated_cost?.total_inr) : "—", sub: report ? `≈ ${fmt(report.estimated_cost?.per_sqft_inr)}/sqft` : "—" },
    { icon: TrendingDown, label: "Potential Cost Optimisation", value: report ? compactINR(savings) : "—", sub: report ? `${(report.optimizations || []).length} opportunities` : "—", color: "#16a34a" },
    { icon: FileText, label: "Uploaded Documents", value: docs.length, sub: "floor plans, BOQ, quotes" },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Recommended next step */}
      <div className="lg:col-span-12 rounded-2xl border border-[#0E9AC8]/25 bg-gradient-to-br from-[#0B1B2B] to-[#0e2c4d] text-white p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="overview-recommended">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl bg-[#0E9AC8]/20 flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5 text-[#7dd3fc]" strokeWidth={1.75} />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#7dd3fc]">Recommended next step</p>
            <p className="mt-1.5 font-display text-lg font-bold text-white">{rec.label}</p>
            <p className="mt-1 text-sm text-white/70 leading-relaxed max-w-2xl">{rec.desc}</p>
          </div>
        </div>
        <Button data-testid="overview-recommended-cta" onClick={rec.act}
          className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-11 px-6 shrink-0 transition-colors">
          {rec.cta} <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
        </Button>
      </div>

      {/* KPI tiles */}
      <div className="lg:col-span-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t) => (
          <div key={t.label} data-testid={`overview-${t.label}`} className={`${CARD} p-6`}>
            <div className="h-10 w-10 rounded-lg bg-[#0E9AC8]/10 flex items-center justify-center mb-4">
              <t.icon className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.6} />
            </div>
            <p className="font-display text-3xl font-extrabold tracking-tight" style={{ color: t.color || "#0F172A" }}>{t.value}</p>
            <p className="mt-1 text-sm font-medium text-slate-700">{t.label}</p>
            <p className="text-xs text-slate-400 mt-0.5">{t.sub}</p>
          </div>
        ))}
      </div>

      {/* Timeline */}
      <div className={`lg:col-span-5 ${CARD} p-7`}>
        <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Clock className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.75} /> Project Timeline</h3>
        <div className="mt-6 relative pl-6">
          <div className="absolute left-[7px] top-1 bottom-1 w-px bg-slate-200" />
          {timeline.map((e, i) => (
            <div key={i} className="relative pb-6 last:pb-0">
              <span className={`absolute -left-6 top-0.5 h-3.5 w-3.5 rounded-full border-2 ${e.done ? "bg-[#0E9AC8] border-[#0E9AC8]" : "bg-white border-slate-300"}`} />
              <p className={`text-sm font-semibold ${e.done ? "text-slate-900" : "text-slate-400"}`}>{e.label}</p>
              <p className="text-xs text-slate-400">{e.done ? (e.date ? format(new Date(e.date), "PP") : "") : "Pending"}{e.hint ? ` · ${e.hint}` : ""}</p>
            </div>
          ))}
        </div>
      </div>

      {/* AI Insights */}
      <div className={`lg:col-span-7 ${CARD} p-7`}>
        <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.75} /> AI Insights</h3>
        {report ? (
          <>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed">{report.summary}</p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Top opportunity</p>
                {(report.optimizations || []).slice(0, 1).map((o, i) => (
                  <div key={i} className="border-l-2 border-emerald-500 pl-3">
                    <p className="text-sm font-semibold text-slate-800">{o.title}</p>
                    {o.potential_saving_inr > 0 && <p className="text-xs text-emerald-600 font-bold">Save {fmt(o.potential_saving_inr)}</p>}
                  </div>
                ))}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Top risk</p>
                {(report.risks || []).slice(0, 1).map((r, i) => (
                  <div key={i} className="border-l-2 border-red-500 pl-3">
                    <p className="text-sm font-semibold text-slate-800">{r.title}</p>
                    <p className="text-xs text-slate-500 capitalize">{r.severity} severity</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="mt-4">
            <p className="text-sm text-slate-500">No report yet. Generate your Construction Health Report to unlock AI insights.</p>
            <Button data-testid="overview-generate-cta" onClick={onGenerate} className="mt-4 bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl transition-colors">
              <Sparkles className="mr-2 h-4 w-4" /> Generate Report
            </Button>
          </div>
        )}
      </div>

      {/* Upcoming tasks */}
      <div className={`lg:col-span-7 ${CARD} p-7`}>
        <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><ListChecks className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.75} /> Upcoming Tasks</h3>
        {report && (report.next_steps || []).length ? (
          <div className="mt-4 space-y-3">
            {(report.next_steps || []).slice(0, 5).map((s, i) => (
              <div key={i} className="flex items-start gap-3">
                <CircleDot className="h-4 w-4 text-[#0E9AC8] mt-0.5 shrink-0" strokeWidth={1.75} />
                <p className="text-sm text-slate-700 leading-relaxed">{s}</p>
              </div>
            ))}
          </div>
        ) : <p className="mt-4 text-sm text-slate-500">Recommended next steps will appear here after you generate your report.</p>}
      </div>

      {/* Consultation status */}
      <div className={`lg:col-span-5 ${CARD} p-7 flex flex-col`}>
        <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><CalendarIcon className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.75} /> Consultation Status</h3>
        {latestConsult ? (
          <div className="mt-4">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              <CheckCircle2 className="h-3.5 w-3.5" /> {latestConsult.status}
            </span>
            <p className="mt-3 text-sm text-slate-600 capitalize">{latestConsult.mode} consultation</p>
            <p className="text-sm text-slate-500">Preferred: {format(new Date(latestConsult.preferred_date), "PPP")}</p>
          </div>
        ) : (
          <div className="mt-4 flex-1 flex flex-col">
            <p className="text-sm text-slate-500">No consultation booked yet. Talk to an independent expert about your project.</p>
            <Button data-testid="overview-book-cta" onClick={onBook} variant="outline" className="mt-4 rounded-xl border-slate-300 hover:border-[#0E9AC8] hover:text-[#0E9AC8] transition-colors w-fit">
              Book Consultation
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ReportView({ report, onRegenerate, generating }) {
  const scoreColor = report.overall_score >= 80 ? "#16a34a" : report.overall_score >= 60 ? "#0E9AC8" : "#dc2626";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className={`lg:col-span-4 ${CARD} p-8 flex flex-col`} data-testid="report-score">
        <p className="text-xs uppercase tracking-wide text-slate-500">Overall Health Score</p>
        <div className="flex items-end gap-2 mt-3">
          <span className="font-display text-6xl font-extrabold tracking-tighter" style={{ color: scoreColor }}>{report.overall_score}</span>
          <span className="text-2xl text-slate-300 font-display font-bold mb-1">/100</span>
        </div>
        <span className="mt-2 inline-block w-fit text-sm font-semibold px-3 py-1 rounded-full border" style={{ color: scoreColor, borderColor: scoreColor + "55" }}>{report.score_label}</span>
        <Progress value={report.overall_score} className="mt-4 h-2" />
        <p className="mt-5 text-sm leading-relaxed text-slate-600">{report.summary}</p>
        <Button data-testid="regenerate-report-button" onClick={onRegenerate} disabled={generating} variant="outline"
          className="mt-auto rounded-xl border-slate-300 hover:border-[#0E9AC8] hover:text-[#0E9AC8] transition-colors">
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-2 h-4 w-4" /> Regenerate</>}
        </Button>
      </div>

      <div className={`lg:col-span-4 ${CARD} p-8`}>
        <p className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><IndianRupee className="h-3.5 w-3.5" /> Estimated Construction Cost</p>
        <p className="font-display text-4xl font-extrabold tracking-tighter text-slate-900 mt-3">{fmt(report.estimated_cost?.total_inr)}</p>
        <p className="text-sm text-slate-500 mt-1">≈ {fmt(report.estimated_cost?.per_sqft_inr)} / sq ft</p>
        <p className="text-xs text-slate-400 mt-3 leading-relaxed">{report.estimated_cost?.basis}</p>
      </div>

      <div className={`lg:col-span-4 ${CARD} p-8`}>
        <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Cost Breakdown</p>
        <div className="h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={report.cost_breakdown || []} dataKey="amount_inr" nameKey="category" innerRadius={45} outerRadius={75} paddingAngle={2}>
                {(report.cost_breakdown || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmt(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 space-y-1">
          {(report.cost_breakdown || []).map((c, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-slate-600"><span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{c.category}</span>
              <span className="text-slate-400">{c.percent}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className={`lg:col-span-6 ${CARD} p-8`} data-testid="report-optimizations">
        <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><TrendingDown className="h-5 w-5 text-emerald-600" strokeWidth={1.75} /> Optimization Opportunities</h3>
        <div className="mt-4 space-y-4">
          {(report.optimizations || []).map((o, i) => (
            <div key={i} className="border-l-2 border-emerald-500 pl-4">
              <div className="flex items-center justify-between gap-3">
                <p className="font-semibold text-slate-800 text-sm">{o.title}</p>
                {o.potential_saving_inr > 0 && <span className="text-xs font-bold text-emerald-600 whitespace-nowrap">Save {fmt(o.potential_saving_inr)}</span>}
              </div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{o.detail}</p>
            </div>
          ))}
        </div>
      </div>

      <div className={`lg:col-span-6 ${CARD} p-8`} data-testid="report-risks">
        <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" strokeWidth={1.75} /> Identified Risks</h3>
        <div className="mt-4 space-y-3">
          {(report.risks || []).map((r, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${sevColor[r.severity] || sevColor.low}`}>{r.severity}</span>
              <div>
                <p className="font-semibold text-slate-800 text-sm">{r.title}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{r.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-12 bg-slate-900 rounded-2xl text-white p-8" data-testid="report-next-steps">
        <h3 className="font-display text-lg font-bold flex items-center gap-2"><ListChecks className="h-5 w-5 text-[#F5923C]" strokeWidth={1.75} /> Recommended Next Steps</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {(report.next_steps || []).map((s, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="font-display font-bold text-[#F5923C] shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <p className="text-sm text-white/85 leading-relaxed">{s}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DocumentsTab({ projectId, docs, onChange, onAnalyze }) {
  const [category, setCategory] = useState("floor_plan");
  const [uploading, setUploading] = useState(false);
  const [justUploaded, setJustUploaded] = useState(null);
  const fileRef = useRef();

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      const res = await api.post(`/projects/${projectId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJustUploaded(res.data);
      toast.success("Document uploaded");
      onChange();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (docId) => {
    await api.delete(`/documents/${docId}`);
    toast.success("Document removed");
    onChange();
  };

  const iconFor = (ct) => ct?.includes("image") ? ImageIcon : (ct?.includes("sheet") || ct?.includes("csv") || ct?.includes("excel")) ? FileSpreadsheet : FileText;

  return (
    <div className="space-y-6">
      {justUploaded && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="upload-success-banner">
          <div className="flex items-start gap-3.5">
            <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-slate-900">Document uploaded successfully</p>
              <p className="text-sm text-slate-600 mt-0.5 truncate">NCIS™ is ready to analyse <b className="text-slate-800">{justUploaded.original_filename}</b>.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" data-testid="upload-success-dismiss" onClick={() => setJustUploaded(null)}
              className="rounded-xl text-slate-500 hover:text-slate-700">Not now</Button>
            <Button data-testid="analyse-with-ncis-button" onClick={() => { const d = justUploaded; setJustUploaded(null); onAnalyze && onAnalyze(d); }}
              className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl transition-colors">
              <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.75} /> Analyse with NCIS™
            </Button>
          </div>
        </div>
      )}

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className={`lg:col-span-1 ${CARD} p-6 h-fit`}>
        <h3 className="font-display text-lg font-bold text-slate-900">Upload Document</h3>
        <p className="text-sm text-slate-500 mt-1">PDF, images, Excel or CSV.</p>
        <Label className="mt-4 block">Category</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="doc-category-select" className="rounded-xl mt-1.5"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="floor_plan">Architectural / Floor Plan</SelectItem>
            <SelectItem value="structural">Structural Drawing</SelectItem>
            <SelectItem value="boq">BOQ</SelectItem>
            <SelectItem value="quotation">Quotation</SelectItem>
            <SelectItem value="soil_report">Soil Report</SelectItem>
            <SelectItem value="image">Site Image</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        <input ref={fileRef} type="file" data-testid="file-input" onChange={onUpload}
          accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls,.txt" className="hidden" />
        <Button data-testid="upload-button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="mt-4 w-full bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl transition-colors">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-2 h-4 w-4" /> Choose file</>}
        </Button>
      </div>

      <div className="lg:col-span-2">
        {docs.length === 0 ? (
          <div className={`${CARD} p-16 text-center h-full flex flex-col items-center justify-center`} data-testid="docs-empty">
            <FileText className="h-10 w-10 text-slate-300 mb-3" strokeWidth={1.25} />
            <p className="text-slate-500">No documents uploaded yet.</p>
          </div>
        ) : (
          <div className={`${CARD} divide-y divide-slate-100 overflow-hidden`}>
            {docs.map((d) => {
              const Icon = iconFor(d.content_type);
              return (
                <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center justify-between gap-4 p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-lg bg-[#0E9AC8]/10 flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{d.original_filename}</p>
                      <p className="text-xs text-slate-400 uppercase tracking-wide">{d.category.replace("_", " ")}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" data-testid={`analyse-doc-${d.id}`} onClick={() => onAnalyze && onAnalyze(d)}
                      className="text-[#0E9AC8] hover:text-[#0B7EA4] hover:bg-[#0E9AC8]/10 rounded-lg text-xs font-semibold px-2.5">
                      <Sparkles className="h-3.5 w-3.5 mr-1" strokeWidth={1.75} /> Analyse
                    </Button>
                    <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                      className="text-xs text-slate-500 hover:text-[#0E9AC8] px-3 transition-colors">View</a>
                    <Button variant="ghost" size="sm" data-testid={`delete-doc-${d.id}`} onClick={() => del(d.id)} className="text-slate-400 hover:text-red-600 rounded-lg">
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

function EstimatesTab({ projectId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get(`/estimate/list?project_id=${projectId}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [projectId]);

  const download = async (eid, name, kind, fmt) => {
    const key = `${eid}-${kind}-${fmt}`;
    setBusy(key);
    try {
      const res = await api.get(`/estimate/${eid}/download?kind=${kind}&fmt=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${(name || "estimate").replace(/\s+/g, "_")}_${kind}.${fmt === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
    finally { setBusy(""); }
  };

  if (items === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0E9AC8]" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900">Saved Estimates</h3>
          <p className="text-sm text-slate-500">Cost estimates you generated in the NCIS Estimate Studio for this project.</p>
        </div>
        <Button data-testid="estimates-open-studio" onClick={() => navigate("/estimate")} className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl">
          <Calculator className="h-4 w-4 mr-2" /> Open Estimate Studio
        </Button>
      </div>
      {items.length === 0 ? (
        <div className={`${CARD} p-16 text-center`} data-testid="estimates-empty">
          <Calculator className="h-10 w-10 text-slate-300 mx-auto mb-3" strokeWidth={1.25} />
          <p className="text-slate-500">No estimates saved yet. Build one in the Estimate Studio and save it here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((e) => (
            <div key={e.id} data-testid={`estimate-card-${e.id}`} className={`${CARD} p-6`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-display text-2xl font-extrabold text-slate-900">{compactINR(e.totals?.total)}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{e.inputs?.rate_name} · {Math.round(e.inputs?.bua || 0).toLocaleString("en-IN")} sqft · {e.inputs?.floors} floor(s)</p>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#0E9AC8]/10 text-[#0E9AC8]">
                  <ShieldCheck className="h-3.5 w-3.5" /> {e.confidence?.score}%
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-3">{e.created_at ? format(new Date(e.created_at), "PPp") : ""}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-xl" onClick={() => download(e.id, e.project_name, "estimate", "pdf")} disabled={busy === `${e.id}-estimate-pdf`}>
                  {busy === `${e.id}-estimate-pdf` ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileDown className="h-4 w-4 mr-1" /> PDF</>}
                </Button>
                <Button size="sm" variant="outline" className="rounded-xl" onClick={() => download(e.id, e.project_name, "estimate", "excel")} disabled={busy === `${e.id}-estimate-excel`}>
                  {busy === `${e.id}-estimate-excel` ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</>}
                </Button>
                {e.boq_paid && (
                  <Button size="sm" className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white" onClick={() => download(e.id, e.project_name, "boq", "pdf")} disabled={busy === `${e.id}-boq-pdf`}>
                    {busy === `${e.id}-boq-pdf` ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileDown className="h-4 w-4 mr-1" /> BOQ PDF</>}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

let lastAnalyzedNonce = null;
const CHAT_SUGGESTIONS = (cat) => {
  if (cat === "boq" || cat === "quotation") return ["Is this reasonable?", "What items are missing?", "Which items look expensive?", "Can I reduce the cost?", "What should I ask the contractor?"];
  if (cat === "floor_plan" || cat === "structural") return ["What's the built-up area?", "Any design red flags?", "Is the layout efficient?", "What to check with my architect?"];
  if (cat === "soil_report") return ["What does this mean for my foundation?", "Any risks I should know?", "What should I do next?"];
  return ["What should my construction cost?", "Any risks I should know?", "What should I do next?"];
};
const analysisPrompt = (doc) => {
  const n = doc.original_filename, c = doc.category;
  if (c === "boq") return `Please analyse my uploaded BOQ "${n}". Is it reasonable for my project? Flag any missing items, items that look expensive, and where I could reduce cost. Keep it practical.`;
  if (c === "quotation") return `Please analyse my uploaded quotation "${n}". Is it reasonable? What's missing, what looks inflated, and what should I negotiate or ask the contractor?`;
  if (c === "floor_plan" || c === "structural") return `Please review my uploaded drawing "${n}". Tell me the built-up area, room-wise areas if available, and any observations or red flags.`;
  if (c === "soil_report") return `Please review my uploaded soil report "${n}" and explain what it means for my foundation and construction approach.`;
  return `Please review my uploaded document "${n}" and share the most relevant observations for my project.`;
};

function ChatTab({ projectId, projectName, onDocUploaded, focusDoc, onFocusDoc, pendingAnalyze, onAnalyzed }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef();
  const attachRef = useRef();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { api.get(`/projects/${projectId}/chat`).then((res) => setMessages(res.data)).catch(() => {}).finally(() => setLoaded(true)); }, [projectId]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const streamReply = async (text) => {
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ project_id: projectId, message: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          const data = JSON.parse(part.slice(5).trim());
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + data.delta };
              return copy;
            });
          }
        }
      }
    } catch {
      toast.error("Chat failed");
    } finally {
      setStreaming(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    await streamReply(text);
  };

  const ask = (q) => { if (!streaming && !uploading) streamReply(q); };

  // When the user taps "Analyse with NCIS™" on a document, auto-start an analysis of it.
  // Nonce guard (module-level) survives React StrictMode remounts to prevent double/missed fires.
  useEffect(() => {
    if (loaded && pendingAnalyze && lastAnalyzedNonce !== pendingAnalyze.nonce) {
      lastAnalyzedNonce = pendingAnalyze.nonce;
      streamReply(analysisPrompt(pendingAnalyze.doc));
      onAnalyzed && onAnalyzed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAnalyze, loaded]);

  const isLayout = (name) => /plan|layout|drawing|floor/i.test(name || "");

  const onAttach = async (e) => {
    const file = e.target.files[0];
    if (!file || streaming || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", isLayout(file.name) ? "floor_plan" : "other");
      await api.post(`/projects/${projectId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded to your documents`);
      onDocUploaded && onDocUploaded();
      onFocusDoc && onFocusDoc({ id: `attach-${Date.now()}`, original_filename: file.name, category: isLayout(file.name) ? "floor_plan" : "other" });
      const q = isLayout(file.name)
        ? `I've uploaded my layout/floor plan "${file.name}". Please review it and tell me the built-up area, room-wise areas if available, and any observations.`
        : `I've uploaded "${file.name}" to my project. Please review it and share relevant observations.`;
      await streamReply(q);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (attachRef.current) attachRef.current.value = "";
    }
  };

  const chips = CHAT_SUGGESTIONS(focusDoc?.category);

  return (
    <div className={`${CARD} flex flex-col h-[560px] overflow-hidden`}>
      {/* Context header */}
      <div className="border-b border-slate-200 px-5 py-3 flex items-center gap-3 bg-slate-50/70" data-testid="chat-context-header">
        <div className="h-9 w-9 rounded-lg bg-[#0E9AC8] flex items-center justify-center shrink-0">
          <Bot className="h-5 w-5 text-white" strokeWidth={1.75} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0E9AC8]">NCIS™ analysing your project</p>
          <p className="text-sm font-medium text-slate-800 truncate">
            {projectName}{focusDoc ? <span className="text-slate-500"> · {focusDoc.original_filename}</span> : ""}
          </p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-5">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <div className="h-12 w-12 rounded-2xl bg-[#0E9AC8]/10 flex items-center justify-center mx-auto mb-3">
              <Bot className="h-6 w-6 text-[#0E9AC8]" strokeWidth={1.5} />
            </div>
            <p className="font-display font-bold text-slate-900">What would you like to know?</p>
            <p className="text-sm text-slate-500 mt-1">Pick a question below, attach a document, or ask anything.</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`chat-msg-${m.role}`} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center ${m.role === "user" ? "bg-slate-900" : "bg-[#0E9AC8]"}`}>
              {m.role === "user" ? <UserIcon className="h-4 w-4 text-white" strokeWidth={1.75} /> : <Bot className="h-4 w-4 text-white" strokeWidth={1.75} />}
            </div>
            <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap font-sans ${m.role === "user" ? "bg-slate-900 text-white rounded-tr-sm" : "bg-slate-100 text-slate-800 rounded-tl-sm"}`}>
              {m.content || <Loader2 className="h-4 w-4 animate-spin" />}
            </div>
          </div>
        ))}
      </div>

      {/* Suggested questions */}
      {!streaming && messages.length < 4 && (
        <div className="px-4 pb-1 pt-3 flex flex-wrap gap-2" data-testid="chat-suggestions">
          {chips.map((q) => (
            <button key={q} type="button" data-testid={`chat-suggestion-${q.slice(0, 12)}`} onClick={() => ask(q)}
              className="text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:border-[#0E9AC8] hover:text-[#0E9AC8] px-3.5 py-1.5 rounded-full transition-colors">
              {q}
            </button>
          ))}
        </div>
      )}

      <div className="border-t border-slate-200 p-4 flex gap-2 items-center">
        <input ref={attachRef} type="file" data-testid="chat-attach-input" onChange={onAttach} className="hidden"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" />
        <Button data-testid="chat-attach-button" type="button" variant="outline" onClick={() => attachRef.current?.click()} disabled={uploading || streaming}
          className="rounded-xl border-slate-300 hover:border-[#0E9AC8] hover:text-[#0E9AC8] shrink-0 px-3">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" strokeWidth={1.75} />}
        </Button>
        <Input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type your question…" className="rounded-xl" />
        <Button data-testid="chat-send-button" onClick={send} disabled={streaming}
          className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl transition-colors shrink-0">
          <Send className="h-4 w-4" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}

function ConsultTab({ projectId }) {
  const [pricing, setPricing] = useState({ tiers: [], slots: [] });
  const [tier, setTier] = useState("online_30");
  const [date, setDate] = useState();
  const [slot, setSlot] = useState("");
  const [avail, setAvail] = useState({});
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [promo, setPromo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/consultations/pricing").then((r) => setPricing(r.data)); }, []);

  useEffect(() => {
    setSlot("");
    if (!date) { setAvail({}); return; }
    api.get(`/consultations/availability?date=${format(date, "yyyy-MM-dd")}`).then((r) => {
      const m = {};
      r.data.slots.forEach((s) => { m[s.id] = s.available; });
      setAvail(m);
    });
  }, [date]);

  const pkg = pricing.tiers.find((t) => t.id === tier);
  const discountable = pkg?.discountable;
  const promoValid = promo.trim().toUpperCase() === "LAUNCH99";
  const effectivePrice = pkg ? (promoValid && discountable ? 99 : pkg.amount) : 0;
  const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

  const submit = async () => {
    if (!date) { toast.error("Please pick a date"); return; }
    if (!slot) { toast.error("Please select a time slot"); return; }
    if (!phone) { toast.error("Please enter your phone number"); return; }
    if (promo.trim() && !discountable) { toast.error("Promo codes don't apply to on-site consultations"); return; }
    setSaving(true);
    try {
      await payWithRazorpay({
        orderPath: "/consultations/razorpay-order",
        orderBody: {
          project_id: projectId, tier, preferred_date: format(date, "yyyy-MM-dd"), slot,
          phone, message, promo_code: promo.trim(),
        },
        description: pkg?.label || "Consultation",
        onSuccess: () => { toast.success("Payment successful! Your consultation is booked."); navigate("/dashboard"); },
        onFailure: (e) => { if (e?.message !== "cancelled") toast.error(e?.response?.data?.detail || "Payment could not be completed"); setSaving(false); },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment. Try again.");
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="relative rounded-2xl overflow-hidden min-h-[300px] hidden lg:block">
        <img src="https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwbW9kZXJuJTIwaG91c2UlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODU3MzgyMjR8MA&ixlib=rb-4.1.0&q=85"
          alt="Consultation" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 to-slate-900/20" />
        <div className="absolute bottom-0 p-8">
          <h3 className="font-display text-2xl font-extrabold text-white tracking-tight">Talk to a real expert.</h3>
          <p className="text-white/85 mt-2 text-sm leading-relaxed max-w-sm">Paid consultations ensure you get focused, independent expert advice. Two slots available daily.</p>
          <div className="mt-4 space-y-1.5">
            {pricing.tiers.map((t) => (
              <div key={t.id} className="flex items-center justify-between text-sm text-white/90 max-w-xs">
                <span>{t.label}</span><span className="font-semibold">{inr(t.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`${CARD} p-8`}>
        <h3 className="font-display text-xl font-bold text-slate-900">Book a Consultation</h3>
        <div className="mt-6 space-y-5">
          <div>
            <Label>Consultation type</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger data-testid="consult-tier-select" className="rounded-xl mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {pricing.tiers.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label} — {inr(t.amount)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Preferred date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button data-testid="consult-date-button" variant="outline" className="w-full justify-start rounded-xl mt-1.5 font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  {date ? format(date, "PPP") : <span className="text-slate-400">Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-xl" align="start">
                <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-slate-400" /> Time slot</Label>
            <div className="grid grid-cols-2 gap-3 mt-1.5">
              {pricing.slots.map((s) => {
                const isAvail = !date || avail[s.id] !== false;
                const selected = slot === s.id;
                return (
                  <button key={s.id} type="button" data-testid={`slot-${s.id}`}
                    disabled={!date || !isAvail}
                    onClick={() => setSlot(s.id)}
                    className={`rounded-xl border px-3 py-3 text-sm font-medium transition-colors text-left
                      ${selected ? "border-[#0E9AC8] bg-[#0E9AC8]/10 text-[#0E9AC8]" : "border-slate-200 text-slate-700 hover:border-[#0E9AC8]"}
                      ${(!date || !isAvail) ? "opacity-40 cursor-not-allowed hover:border-slate-200" : ""}`}>
                    {s.label}
                    {date && !isAvail && <span className="block text-[10px] text-red-500 mt-0.5">Booked</span>}
                  </button>
                );
              })}
            </div>
            {!date && <p className="text-xs text-slate-400 mt-1.5">Pick a date to see available slots.</p>}
          </div>

          <div>
            <Label>Phone number</Label>
            <Input data-testid="consult-phone" className="rounded-xl mt-1.5" placeholder="+91 98765 43210" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>

          <div>
            <Label>Message (optional)</Label>
            <Textarea data-testid="consult-message" className="rounded-xl mt-1.5" placeholder="What would you like to discuss?" value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>

          <div>
            <Label>Promo code {discountable ? "" : <span className="text-xs text-slate-400">(not valid for on-site)</span>}</Label>
            <Input data-testid="consult-promo" disabled={!discountable} className="rounded-xl mt-1.5 uppercase"
              placeholder={discountable ? "e.g. LAUNCH99" : "Not applicable"} value={promo} onChange={(e) => setPromo(e.target.value)} />
            {promoValid && discountable && <p className="text-xs text-emerald-600 mt-1.5">Launch offer applied — pay just ₹99!</p>}
          </div>

          <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
            <span className="text-sm text-slate-500">Amount payable</span>
            <span className="font-display text-xl font-extrabold text-slate-900">
              {promoValid && discountable && pkg && <span className="text-sm font-normal text-slate-400 line-through mr-2">{inr(pkg.amount)}</span>}
              {inr(effectivePrice)}
            </span>
          </div>

          <Button data-testid="consult-submit-button" onClick={submit} disabled={saving}
            className="w-full bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-11 transition-colors">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><IndianRupee className="mr-1 h-4 w-4" strokeWidth={2} /> Pay {inr(effectivePrice)} &amp; Book</>}
          </Button>
          <p className="text-xs text-slate-400 text-center">Secure payment via Razorpay · UPI, cards &amp; netbanking supported.</p>
        </div>
      </div>
    </div>
  );
}

